import styled from 'styled-components';
import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { push, push as Push } from 'redux-first-history';
import {
    ContentLoader,
    CustomTableRowsProps,
    DotsIcon,
    SimplePopover,
    TableRowNameLink,
    Typography,
} from '@dev/ee-components';
import { AccessLevel, JourneyStatus, JourneySummary } from '@dev/ee-api/ee-api-models';
import { map } from 'lodash-es';

import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { getAutomationStore } from '@/store/automations/actions';
import { getAccountStore } from '@/store/account/actions';
import { DropdownOptions } from '@/store/automations/types';
import { AutomationPaths } from '@/services/dictionaries/Paths';
import { formatNumber } from '@/services/tools/formatNumber';
import { StatusBadge } from '@/assets/StatusBadge';

import { ViewReportsProps } from '../../AutomationListInterface';
import { useDropdownActions } from '../useDropdownActions';

const TableItemButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    padding-left: 25px;
    align-items: center;
`;

const getRowPopoverOptions = (status: JourneyStatus, t: any): Record<string, string>[] => [
    {
        label: t('viewworkflow'),
        value: DropdownOptions.ViewWorkflow,
    },
    ...(status === JourneyStatus.Active
        ? [
              {
                  label: t('pause'),
                  value: DropdownOptions.Pause,
              },
              {
                  label: t('edit'),
                  value: DropdownOptions.PauseAndEdit,
              },
          ]
        : []),
    ...(status === JourneyStatus.Paused
        ? [
              {
                  label: t('reactivate'),
                  value: DropdownOptions.Reactivate,
              },
              {
                  label: t('edit'),
                  value: DropdownOptions.Edit,
              },
          ]
        : []),
    ...(status === JourneyStatus.Invalid
        ? [
              {
                  label: t('edit'),
                  value: DropdownOptions.Edit,
              },
          ]
        : []),
    {
        label: t('delete'),
        color: '#ff0000',
        value: DropdownOptions.Delete,
    },
];

const ViewReports: FC<ViewReportsProps> = (props: ViewReportsProps): JSX.Element => {
    const dispatch = useDispatch();

    return (
        <div
            onClick={(ev): void => {
                ev.stopPropagation();
                dispatch(Push(AutomationPaths.AutomationReport.replace(':name', safelyEncodeBase64Url(props.name))));
            }}
        >
            <Typography variant="body1" bodyVariant="link">
                {props.translation}
            </Typography>
        </div>
    );
};

export const useTableRows = (): CustomTableRowsProps[] => {
    const { t } = useTranslation();
    const automations = useSelector(getAutomationStore);
    const { pagination } = automations;
    const dispatch = useDispatch();
    const { GeneralInfo } = useSelector(getAccountStore);
    const accountAl = GeneralInfo.AccountAccessLevel;

    const dropdownActions = useDropdownActions();

    if (automations?.loading) {
        const perPage = pagination?.perPage || 5;
        const rows: CustomTableRowsProps[] = [];

        for (let index = 0; index < perPage; index++) {
            rows?.push({
                to: <ContentLoader mode="row" height={20} />,
                logsDate: <ContentLoader mode="row" height={20} />,
                emptyColumn: <div />,
                event: <ContentLoader mode="row" height={20} />,
                bounce: <ContentLoader mode="row" height={20} />,
                icon: <ContentLoader mode="row" height={20} />,
            });
        }

        return rows;
    }

    return map(automations?.collection, (automation: JourneySummary): CustomTableRowsProps => {
        const rowPopoverOptions = getRowPopoverOptions(automation.Status, t);

        return {
            automationName: (
                <div style={{ wordBreak: 'break-all', paddingRight: '10px' }}>
                    <Typography variant="caption">
                        <TableRowNameLink
                            onClick={(): void => {
                                dispatch(
                                    push(
                                        AutomationPaths.AutomationEditor.replace(
                                            ':name',
                                            safelyEncodeBase64Url(automation.Name)
                                        )
                                    )
                                );
                            }}
                        >
                            {automation.Name}
                        </TableRowNameLink>
                    </Typography>
                </div>
            ),
            opened: <Typography variant="caption">{formatNumber(automation.TotalOpened)}</Typography>,
            clicked: <Typography variant="caption">{formatNumber(automation.TotalClicked)}</Typography>,
            status: <StatusBadge status={automation.Status} value={automation.Status} />,
            viewreport: (
                <TableItemButtonsWrapper>
                    <div>
                        <ViewReports name={automation.Name} translation={t('viewreport')} />
                    </div>
                    {accountAl?.includes?.(AccessLevel.ModifyAutomations) && (
                        <div
                            onClick={(ev): void => {
                                ev.stopPropagation();
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <SimplePopover
                                type="icon"
                                options={rowPopoverOptions}
                                enableCheckbox={false}
                                onItemClick={(ev, option: { label: string; value: DropdownOptions }): void => {
                                    dropdownActions(option, automation);
                                }}
                            >
                                <DotsIcon />
                            </SimplePopover>
                        </div>
                    )}
                </TableItemButtonsWrapper>
            ),
            rowID: automation.Name,
        };
    });
};
